<template>
  <div>
    <BaseCard
      v-if="state === states.READY"
      data-test="candidate-usage"
    >
      <BaseCardHeader
        bg="bg-secondary"
        class="mb-6"
      >
        <template slot="icon">
          <Calculator />
        </template>
        Usage
      </BaseCardHeader>

      <section>
        <h4 class="mb-1 text-lg font-medium">
          This month
        </h4>
        <p class="text-sm text-gray-600">
          {{ monthLabel }}
        </p>
        <div class="grid gap-4 mt-6 sm:grid-cols-3">
          <div class="px-4 py-3 border rounded">
            <p class="text-xs font-medium tracking-wider text-gray-600 uppercase">
              Candidates unlocked
            </p>
            <p
              class="mt-2 text-2xl font-bold"
              data-test="month-candidates"
            >
              {{ month.candidates }}
            </p>
            <p
              v-if="month.free > 0"
              class="text-sm text-gray-600"
            >
              {{ month.free }} free
            </p>
          </div>
          <div class="px-4 py-3 border rounded">
            <p class="text-xs font-medium tracking-wider text-gray-600 uppercase">
              Charged
            </p>
            <p
              class="mt-2 text-2xl font-bold"
              data-test="month-charged"
            >
              {{ month.charged }}
            </p>
            <p class="text-sm text-gray-600">
              at {{ unitAmount }} each
            </p>
          </div>
          <div class="px-4 py-3 border rounded">
            <p class="text-xs font-medium tracking-wider text-gray-600 uppercase">
              Next invoice
            </p>
            <p
              class="mt-2 text-2xl font-bold"
              data-test="month-amount"
            >
              {{ month.amountFormatted }}
            </p>
            <p
              class="text-sm text-gray-600"
              data-test="next-invoice"
            >
              <span v-if="nextInvoice">on {{ formatDate(nextInvoice) }}</span>
              <span v-else-if="!billing.has_card">when you add a card</span>
              <span v-else>at the end of the month</span>
              <span v-if="taxPercent"> + {{ taxPercent }}% VAT</span>
            </p>
          </div>
        </div>
        <p class="mt-6 text-sm text-gray-600">
          A candidate counts once, however many tests are in the assessment, when you unlock their results. Results for your first {{ billing.free_candidates }} candidates were free; you have unlocked {{ billing.candidates_unlocked }} {{ billing.candidates_unlocked === 1 ? 'candidate' : 'candidates' }} in total.
        </p>
        <p
          v-if="billing.blocked"
          class="mt-4 text-sm text-red-700"
        >
          Your last payment failed, so new results are locked until it is settled.
          <router-link
            :to="{ name: 'settings-payg-plan' }"
            class="underline"
          >
            Update your card
          </router-link>
        </p>
      </section>
    </BaseCard>
    <BaseCard
      v-else-if="state === states.LOADING"
      class="my-8"
    >
      <Loader />
    </BaseCard>
    <BaseCard v-else-if="state === states.ERROR">
      <BaseErrorBlock />
    </BaseCard>
  </div>
</template>

<script>
import Calculator from '@components/Icons/Calculator'
import Loader from '@components/Loader'

import states from '@api/states'
import { formatDate } from '@utils/formatDate'
import { mapGetters } from 'vuex'
import { monthSummary, nextInvoiceDate, formatMinor } from '@utils/candidateBilling'

/**
 * This month's candidates and the next invoice for organisations billed
 * per candidate. Replaces the credits table.
 */
export default {
  components: {
    Calculator,
    Loader
  },

  data() {
    return {
      states,
      formatDate,
      error: null
    }
  },

  computed: {
    ...mapGetters({
      billing: 'subscriptions/candidateBilling',
      hasBillingLoaded: 'subscriptions/hasBillingLoaded'
    }),

    state() {
      if (this.error) {
        return states.ERROR
      }
      if (!this.hasBillingLoaded || !this.billing) {
        return states.LOADING
      }
      return states.READY
    },

    month() {
      return monthSummary(this.billing) || { candidates: 0, free: 0, charged: 0, amount: 0, amountFormatted: formatMinor(0, this.billing.currency) }
    },

    monthLabel() {
      const from = this.billing && this.billing.this_month ? new Date(this.billing.this_month.from) : new Date()
      return from.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' })
    },

    unitAmount() {
      return this.billing.unit_amount ? formatMinor(this.billing.unit_amount, this.billing.currency) : ''
    },

    nextInvoice() {
      return nextInvoiceDate(this.billing)
    },

    taxPercent() {
      return this.billing && this.billing.subscription ? this.billing.subscription.tax_percent : null
    }
  },

  mounted() {
    this.$store.dispatch('subscriptions/getCandidateBilling')
      .catch(error => {
        this.error = error
      })
  }
}
</script>
